/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RequestSelector from './RequestSelector';
import DynamicRequestForm from '../../shared/requests/DynamicRequestForm';
import DynamicRequestView from '../../shared/requests/DynamicRequestView';
import { requestStatusColor, requestStatusLabel } from '../../../utils/requestStatus';
import { toast } from 'react-hot-toast';
import '../../../styles/Requests.css';

const NewRequest = ({
  projects,
  selectedFacility,
  setSelectedFacility,
  facilities,
  loading,
  error
}) => {
  const [activeProject, setActiveProject] = useState(null);

  if (activeProject) {
    return (
      <RequestSelector
        project={activeProject}
        onBack={() => setActiveProject(null)}
        onSubmitted={() => setActiveProject(null)}
      />
    );
  }

  return (
    <div className="new-request">
      <Typography variant="h4" component="h2" gutterBottom>
        Make New Request
      </Typography>

      <Box sx={{ mb: 4 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project Title</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>PI</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project._id}>
                  <TableCell>{project.project_title}</TableCell>
                  <TableCell>{project.facility?.facility_name}</TableCell>
                  <TableCell>{project.project_start_date ? new Date(project.project_start_date).toLocaleDateString() : 'Not set'}</TableCell>
                  <TableCell>{project.project_end_date ? new Date(project.project_end_date).toLocaleDateString() : 'Not set'}</TableCell>
                  <TableCell>
                    <Chip
                      label={project.project_status}
                      color={project.project_status === 'Approved' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    {project.users?.find(u => u.role === 'PI')?.first_name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setActiveProject(project)}
                    >
                      Submit Request
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

const RequestHistory = ({
  requests,
  loading,
  error,
  facilities,
  selectedFacility,
  setSelectedFacility,
  refresh
}) => {
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // The dialog shows a row from the list, which goes stale as soon as someone
  // posts in the thread, so re-read that one request and refresh the list.
  const reloadViewing = async () => {
    if (!viewing?._id) return;
    try {
      const { data } = await axios.get(`/api/requests/${viewing._id}`);
      setViewing(data);
    } catch (err) {
      console.error('Error reloading request:', err);
    }
    refresh();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`/api/requests/${deleting._id}`);
      toast.success('Request deleted');
      setDeleting(null);
      refresh();
    } catch (err) {
      console.error('Error deleting request:', err);
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  if (editing) {
    return (
      <DynamicRequestForm
        request={editing}
        mode="edit"
        onCancel={() => setEditing(null)}
        onSubmitted={() => { setEditing(null); refresh(); }}
      />
    );
  }

  return (
    <div>
      <Typography variant="h4" component="h2" gutterBottom>
        Request History
      </Typography>

      <Box sx={{ mb: 4 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f8f8f8', borderRadius: 1, mt: 2 }}>
          <Typography variant="h6" color="text.secondary">No requests found</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r._id}>
                  <TableCell>{r.request_id}</TableCell>
                  <TableCell>{r.project_title}</TableCell>
                  <TableCell>{r.request_type}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={requestStatusLabel(r.status)}
                      color={requestStatusColor(r.status)}
                    />
                  </TableCell>
                  <TableCell>{new Date(r.submitted_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => setViewing(r)}>
                        View
                      </Button>
                      {/* No revision cycle: while a request is open the team
                          just edits the form and the change is logged. */}
                      {r.permissions?.can_edit && (
                        <Tooltip title="Edit request form">
                          <IconButton size="small" onClick={() => setEditing(r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {r.permissions?.can_delete && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleting(r)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Request Details
          <IconButton aria-label="Close" onClick={() => setViewing(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewing && (
            <DynamicRequestView
              request={viewing}
              onChanged={reloadViewing}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove the request <strong>{deleting?.request_id}</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const Requests = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSection, setActiveSection] = useState(() => {
    const path = location.pathname.split('/').pop();
    return path === 'history' ? 'history' : 'new';
  });

  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (err) {
      setError('Failed to fetch facilities');
    }
  };

  const fetchOngoingProjects = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedFacility === ''
        ? '/api/projects/ongoing'
        : `/api/projects/ongoing?facility=${selectedFacility}`;
      const response = await axios.get(url);
      setProjects(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch projects');
      setLoading(false);
    }
  }, [selectedFacility]);

  const fetchRequestHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedFacility ? { facility: selectedFacility } : {};
      const response = await axios.get('/api/requests/history', { params });
      setRequests(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching request history:', err);
      setError('Failed to fetch request history');
      setLoading(false);
    }
  }, [selectedFacility]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    navigate(`/requests/${section}`);
  };

  useEffect(() => {
    if (activeSection === 'new') {
      fetchOngoingProjects();
      fetchFacilities();
    } else if (activeSection === 'history') {
      fetchRequestHistory();
      fetchFacilities();
    }
  }, [activeSection, fetchOngoingProjects, fetchRequestHistory]);

  return (
    <div className="requests-container">
      <div className="sidebar">
        <h2>Requests</h2>
        <ul>
          <li>
            <button
              type="button"
              onClick={() => handleSectionChange('new')}
              className={activeSection === 'new' ? 'active' : ''}
              aria-current={activeSection === 'new' ? 'page' : undefined}
            >
              Make New Request
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => handleSectionChange('history')}
              className={activeSection === 'history' ? 'active' : ''}
              aria-current={activeSection === 'history' ? 'page' : undefined}
            >
              Request History
            </button>
          </li>
        </ul>
      </div>
      <div className="content-area">
        {activeSection === 'new' ? (
          <NewRequest
            projects={projects}
            selectedFacility={selectedFacility}
            setSelectedFacility={setSelectedFacility}
            facilities={facilities}
            loading={loading}
            error={error}
          />
        ) : (
          <RequestHistory
            requests={requests}
            loading={loading}
            error={error}
            facilities={facilities}
            selectedFacility={selectedFacility}
            setSelectedFacility={setSelectedFacility}
            refresh={fetchRequestHistory}
          />
        )}
      </div>
    </div>
  );
};

export default Requests;
